import React, { useState } from 'react';
import { Box, Typography, Grid} from '@mui/material';
import { TrendingUp, Briefcase, DollarSign, Users, Target, Gift } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import image1 from '../img/Image.JPEG';
import image2 from '../img/IMG_0692.JPEG';

const JoinUsPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    instagramUrl: '',
    twitterUrl: '',
    tiktokUrl: '',
    bio: ''
  });

  const [charCount, setCharCount] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'bio') setCharCount(value.length);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setSubmitError('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.instagramUrl.trim()) newErrors.instagramUrl = 'At least one social media URL is required';
    if (!formData.bio.trim()) newErrors.bio = 'A bit about your brand is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setSubmitError('');

    try {
      const response = await axios.post('https://lyncfit-backend-deploy-production.up.railway.app/webforms/join-us', formData);
      console.log('Response:', response.data);
      if (response.data && response.data.success) {
        setIsSubmitted(true);
      } else {
        setSubmitError(response.data.message || 'An unexpected error occurred.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit the form. Please try again later.';
      setSubmitError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const textFieldStyle = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    borderRadius: '50px',
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.3s ease',
    marginTop: '8px'
  };

  const textareaStyle = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    borderRadius: '20px',
    fontSize: '16px',
    outline: 'none',
    resize: 'none',
    marginTop: '8px',
    fontFamily: 'inherit'
  };

  const BenefitCard = ({ icon, title, description, gradient }) => (
    <Box
      sx={{
        background: 'white',
        p: 4,
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e5e5',
        transition: 'all 0.3s ease',
      }}
    >
      <Box
        sx={{
          background: gradient,
          p: 2,
          borderRadius: '12px',
          display: 'inline-block',
          mb: 3,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
        {title}
      </Typography>
      <Typography sx={{ color: '#666', lineHeight: 1.6 }}>
        {description}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      <Box sx={{ flex: 1 }}>
        <Box sx={{ width: '100vw', bgcolor: '#f5f5f5', overflowX: 'hidden' }}>

          <Box
           sx={{
                width: '100vw',
                background: 'linear-gradient(135deg, #3366cc 0%, #2d5aa0 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                px: 0,
                mx: 0,
            }}
          >
            <Box
              sx={{
                maxWidth: '1200px',
                mx: 'auto',
                py: 10,
                px: 3,
                position: 'relative',
                zIndex: 1,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  fontWeight: 'bold',
                  mb: 3,
                  background: 'linear-gradient(to right, white, rgba(255,255,255,0.9))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  m: 0,
                }}
              >
                Become a LyncFit Affiliate
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.25rem',
                  opacity: 0.9,
                  maxWidth: '512px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Partner with us to help your clients achieve their fitness goals while growing your business.
              </Typography>
            </Box>
          </Box>

          {/* Why Partner With LyncFit Section */}
          <Box sx={{ maxWidth: '1200px', mx: 'auto', py: 10, px: 3 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#333',
                mb: 4,
              }}
            >
              Why Partner With LyncFit?
            </Typography>
            <Typography
              sx={{
                fontSize: '1.125rem',
                textAlign: 'center',
                color: '#666',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.7,
                mb: 6,
              }}
            >
              LyncFit isn't just another fitness app; we're building a culture of consistency, community, and connection. 
              Every reward on our platform is earned through real world activity: attending fitness events, forming friendships, 
              and showing up again and again. That means your brand isn't just seen, it's earned.
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#333',
                mb: 6,
              }}
            >
              Here's why you should join us:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 2,
                mb: 6,
                alignItems: 'stretch',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: 'white',
                  p: 6,
                  borderRadius: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100px',
                    height: '100px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    transform: 'translate(40px, -40px)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Users style={{ width: 28, height: 28, marginRight: '12px' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                    Hyper-Engaged Users
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, opacity: 0.95 }}>
                  Our active community consistently pursues fitness goals, making them highly responsive to incentives.
                </Typography>
              </Box>
              
              <Box
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: 'white',
                  p: 6,
                  borderRadius: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '80px',
                    height: '80px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '50%',
                    transform: 'translate(30px, -30px)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Target style={{ width: 28, height: 28, marginRight: '12px' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                    Targeted Visibility
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, opacity: 0.95 }}>
                  Offer discounts based on user behaviour. Your offer appears when they're most engaged.
                </Typography>
              </Box>
              
              <Box
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  color: 'white',
                  p: 6,
                  borderRadius: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '60px',
                    height: '60px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    transform: 'translate(-20px, 20px)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Gift style={{ width: 28, height: 28, marginRight: '12px' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                    Gamified Redemption
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, opacity: 0.95 }}>
                  Offers are unlocked through our in-app reward points system, ensuring your brand is seen as a reward.
                </Typography>
              </Box>
            </Box>
          </Box>


          
           <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Box
              component="img"
              src={image1}
              alt="LyncFit Community"
              sx={{
                width: 500,
                height: 'auto',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
            <Box
              component="img"
              src={image2}
              alt="LyncFit Community"
              sx={{
                width: 500,
                height: 'auto',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Box>

         


          <Box sx={{ maxWidth: '800px', mx: 'auto', py: 10, px: 3 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#333',
                mb: 8,
              }}
            >
              How it works
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 3,
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    flexShrink: 0,
                  }}
                >
                  1
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: '#333',
                      mb: 1,
                      fontSize: '1.25rem',
                    }}
                  >
                    Application
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                    }}
                  >
                    Ready to join us? Just complete the form below—it's quick, easy, and absolutely free!
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 3,
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: '#333',
                      mb: 1,
                      fontSize: '1.25rem',
                    }}
                  >
                    Speak to Sales
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                    }}
                  >
                    Once you're approved, our sales team will reach out to discuss the integration process further.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 3,
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    flexShrink: 0,
                  }}
                >
                  3
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: '#333',
                      mb: 1,
                      fontSize: '1.25rem',
                    }}
                  >
                    Generate Revenue
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                    }}
                  >
                    Allow your clientele to enjoy your discount through the LyncFit app.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>


        {isSubmitted ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center', padding: 3 }}>
            <Typography variant="h2" sx={{ color: '#3366cc', fontWeight: 'bold' }}>Application Submitted!</Typography>
            <Typography variant="body1" sx={{ mt: 2, mb: 4 }}>Thank you for your interest. We will review your application and contact you soon.</Typography>
            <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', borderRadius: '30px', backgroundColor: '#3366cc', color: 'white', border: 'none', cursor: 'pointer' }}>
              Submit Another Application
            </button>
          </Box>
        ) : (
          // <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>
          //   <div id="application" style={{ width: '100vw', background: 'linear-gradient(135deg, #3366cc, #2d5aa0)', padding: '80px 0', marginLeft: 'calc(-50vw + 50%)' }}>
          //     <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          //       <div style={{ background: 'white', borderRadius: '24px', boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)', padding: '48px' }}>
          //         <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          //           <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '16px', margin: '0 0 16px 0' }}>
          //             Apply as a LyncFit Affiliate
          //           </h2>
          //           <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>
          //             Once you've applied, keep an eye on your inbox. We'll be reaching out within the next 2 weeks.
          //           </p>
          //         </div>
          //         <div style={{ display: 'grid', gap: '24px' }}>
          //           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          //             <div>
          //               <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Full name *</label>
          //               <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} style={{ ...textFieldStyle, borderColor: errors.fullName ? '#d32f2f' : 'transparent' }} required />
          //               {errors.fullName && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.fullName}</p>)}
          //             </div>
                      
          //           </div>
          //           <div>
          //               <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Email *</label>
          //               <input type="email" name="email" value={formData.email} onChange={handleInputChange} style={{ ...textFieldStyle, borderColor: errors.email ? '#d32f2f' : 'transparent' }} required />
          //               {errors.email && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.email}</p>)}
          //           </div>
          //           <div>
          //             <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Phone number <span style={{ color: '#999', fontWeight: 'normal' }}>Optional</span></label>
          //             <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} style={{ ...textFieldStyle, borderColor: errors.phone ? '#d32f2f' : 'transparent' }} />
          //             {errors.phone && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.phone}</p>)}
          //           </div>
          //           <div>
          //             <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Instagram account *</label>
          //             <input type="url" name="instagramUrl" value={formData.instagramUrl} onChange={handleInputChange} placeholder="https://" style={{ ...textFieldStyle, borderColor: errors.instagramUrl ? '#d32f2f' : 'transparent' }} required />
          //             {errors.instagramUrl && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.instagramUrl}</p>)}
          //           </div>
          //           <div>
          //             <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>X account <span style={{ color: '#999', fontWeight: 'normal' }}>Optional</span></label>
          //             <input type="url" name="twitterUrl" value={formData.twitterUrl} onChange={handleInputChange} placeholder="https://" style={textFieldStyle} />
          //           </div>
          //           <div>
          //             <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>TikTok account <span style={{ color: '#999', fontWeight: 'normal' }}>Optional</span></label>
          //             <input type="url" name="tiktokUrl" value={formData.tiktokUrl} onChange={handleInputChange} placeholder="https://" style={textFieldStyle} />
          //           </div>
          //           <div>
          //             <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>A bit about your brand *</label>
          //             <textarea name="bio" value={formData.bio} onChange={handleInputChange} maxLength={320} rows={4} placeholder="Tell us about yourself and your audience..." style={{ ...textareaStyle, borderColor: errors.bio ? '#d32f2f' : 'transparent' }} required />
          //             <div style={{ textAlign: 'right', fontSize: '12px', color: '#666', marginTop: '4px' }}>{charCount} / 320</div>
          //             {errors.bio && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.bio}</p>)}
          //           </div>
          //           <div style={{ textAlign: 'center', marginTop: '40px' }}>
          //              {submitError && (<Typography color="error" sx={{ mb: 2 }}>{submitError}</Typography>)}
          //             <button type="submit" disabled={isLoading} style={{ backgroundColor: isLoading ? '#ccc' : '#3366cc', color: 'white', padding: '14px 48px', border: 'none', borderRadius: '30px', fontSize: '16px', fontWeight: '600', cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease' }}>
          //               {isLoading ? 'Submitting...' : 'Submit Application'}
          //             </button>
          //           </div>
          //         </div>
          //       </div>
          //     </div>
          //   </div>
          // </form>

<form 
  onSubmit={handleSubmit} 
  style={{ 
    width: '100%', 
    maxWidth: '600px', 
    margin: '0 auto', 
    padding: '24px', 
    boxSizing: 'border-box' 
  }}
>
  <div 
    id="application" 
    style={{ 
      width: '100%', 
      background: 'linear-gradient(135deg, #3366cc, #2d5aa0)', 
      padding: '80px 0',
      borderRadius: '24px'
    }}
  >
    <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
      
      {/* White box */}
      <div 
        style={{ 
          background: 'white', 
          borderRadius: '24px', 
          boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)', 
          padding: '48px 32px',      // Added horizontal padding
          boxSizing: 'border-box' 
        }}
      >
        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '600px', marginInline: 'auto' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', margin: '0 0 16px 0' }}>
            Apply as a LyncFit Affiliate
          </h2>
          <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>
            Once you've applied, keep an eye on your inbox. We'll be reaching out within the next 2 weeks.
          </p>
        </div>
        
        {/* Inputs container */}
        <div 
          style={{ 
            display: 'grid', 
            gap: '24px', 
            width: '100%', 
            maxWidth: '600px', 
            margin: '0 auto' 
          }}
        >
          {/* Full Name */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Full name *</label>
            <input 
              type="text" 
              name="fullName" 
              value={formData.fullName} 
              onChange={handleInputChange} 
              style={{ ...textFieldStyle, width: '100%', boxSizing: 'border-box', borderColor: errors.fullName ? '#d32f2f' : '#ccc' }} 
              required 
            />
            {errors.fullName && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.fullName}</p>)}
          </div>

          {/* Email */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Email *</label>
            <input 
              type="email" 
              name="email" 
              value={formData.email} 
              onChange={handleInputChange} 
              style={{ ...textFieldStyle, width: '100%', boxSizing: 'border-box', borderColor: errors.email ? '#d32f2f' : '#ccc' }} 
              required 
            />
            {errors.email && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.email}</p>)}
          </div>

          {/* Phone */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Phone number <span style={{ color: '#999', fontWeight: 'normal' }}>Optional</span></label>
            <input 
              type="tel" 
              name="phone" 
              value={formData.phone} 
              onChange={handleInputChange} 
              style={{ ...textFieldStyle, width: '100%', boxSizing: 'border-box', borderColor: errors.phone ? '#d32f2f' : '#ccc' }} 
            />
            {errors.phone && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.phone}</p>)}
          </div>

          {/* Instagram */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>Instagram account *</label>
            <input 
              type="url" 
              name="instagramUrl" 
              value={formData.instagramUrl} 
              onChange={handleInputChange} 
              placeholder="https://" 
              style={{ ...textFieldStyle, width: '100%', boxSizing: 'border-box', borderColor: errors.instagramUrl ? '#d32f2f' : '#ccc' }} 
              required 
            />
            {errors.instagramUrl && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.instagramUrl}</p>)}
          </div>

          {/* X */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>X account <span style={{ color: '#999', fontWeight: 'normal' }}>Optional</span></label>
            <input 
              type="url" 
              name="twitterUrl" 
              value={formData.twitterUrl} 
              onChange={handleInputChange} 
              placeholder="https://" 
              style={{ ...textFieldStyle, width: '100%', boxSizing: 'border-box' }} 
            />
          </div>

          {/* TikTok */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>TikTok account <span style={{ color: '#999', fontWeight: 'normal' }}>Optional</span></label>
            <input 
              type="url" 
              name="tiktokUrl" 
              value={formData.tiktokUrl} 
              onChange={handleInputChange} 
              placeholder="https://" 
              style={{ ...textFieldStyle, width: '100%', boxSizing: 'border-box' }} 
            />
          </div>

          {/* Bio */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#3366cc', marginBottom: '8px' }}>A bit about your brand *</label>
            <textarea 
              name="bio" 
              value={formData.bio} 
              onChange={handleInputChange} 
              maxLength={320} 
              rows={4} 
              placeholder="Tell us about yourself and your audience..." 
              style={{ ...textareaStyle, width: '100%', boxSizing: 'border-box', borderColor: errors.bio ? '#d32f2f' : '#ccc' }} 
              required 
            />
            <div style={{ textAlign: 'right', fontSize: '12px', color: '#666', marginTop: '4px' }}>{charCount} / 320</div>
            {errors.bio && (<p style={{ color: '#d32f2f', fontSize: '12px', margin: '4px 0 0 16px' }}>{errors.bio}</p>)}
          </div>

          {/* Submit */}
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            {submitError && (<Typography color="error" sx={{ mb: 2 }}>{submitError}</Typography>)}
            <button 
              type="submit" 
              disabled={isLoading} 
              style={{ 
                backgroundColor: isLoading ? '#ccc' : '#3366cc', 
                color: 'white', 
                padding: '14px 48px', 
                border: 'none', 
                borderRadius: '30px', 
                fontSize: '16px', 
                fontWeight: '600', 
                cursor: isLoading ? 'not-allowed' : 'pointer', 
                transition: 'all 0.3s ease' 
              }}
            >
              {isLoading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</form>




        )}
      </Box>
      <Footer />
    </Box>
  );
};

export default JoinUsPage;
